import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const http = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

// Network drops and server/proxy failures without an app error code (500/502/503/504) get the same toast + Retry.
export const isServerFailure = (error) => {
  if (!error || error.code === "ERR_CANCELED") return false;
  if (!error.response) return !!error.isAxiosError;
  const d = error.response.data?.detail;
  return error.response.status >= 500 && !(d && typeof d === "object" && d.code);
};

let refreshing = null;
const NO_REFRESH = /^\/auth\/(login|register|refresh|logout|google|forgot-password|reset-password)/;
http.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config || {};
  if (isServerFailure(error)) {
    window.dispatchEvent(new CustomEvent("am:network-error", { detail: { cfg } }));
    return Promise.reject(error);
  }
  if (error.response?.status === 401 && !cfg._retried && !NO_REFRESH.test(cfg.url || "")) {
    cfg._retried = true;
    try {
      refreshing = refreshing || http.post("/auth/refresh").finally(() => { refreshing = null; });
      await refreshing;
      return http(cfg);
    } catch {
      window.dispatchEvent(new Event("am:logged-out"));
    }
  }
  return Promise.reject(error);
});

// Identical mutations already in flight share one request, so a double tap never sends twice.
const inflight = new Map();
const once = (method, url, body) => {
  if (body instanceof FormData) return http.request({ method, url, data: body }).then((r) => r.data);
  const key = `${method} ${url} ${body === undefined ? "" : JSON.stringify(body)}`;
  if (!inflight.has(key)) inflight.set(key, http.request({ method, url, data: body }).then((r) => r.data).finally(() => inflight.delete(key)));
  return inflight.get(key);
};

export const get = (url, params) => http.get(url, { params }).then((r) => r.data);
export const post = (url, body) => once("post", url, body);
export const put = (url, body) => once("put", url, body);
export const patch = (url, body) => once("patch", url, body);
export const del = (url) => once("delete", url);

export const errorText = (t, error) => {
  if (isServerFailure(error)) return t("net.failed");
  const d = error?.response?.data?.detail;
  if (d && typeof d === "object" && d.code) return t(`errors.${d.code}`, d);
  if (Array.isArray(d)) {
    const field = d[0]?.loc?.slice(-1)[0];
    if (field === "email") return t("errors.invalid_email");
    if (field === "password") return t("errors.password_short");
    return t("errors.required");
  }
  return t("common.unknownError");
};

export const assetUrl = (path) => `${BACKEND_URL}${path}`;
